import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, from, map, Observable, of, switchMap } from 'rxjs';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { StorageScope, StorageService, StorageType } from 'src/app/shared/services/storage.service';
import { UnitAdjustmentService } from '../../adjustment-grid/service/unit-adjustment.service';
import { AsyncDataState } from '../../async/state/async-data.state';
import { LongTermRentalState } from '../../long-term-rental/state/long-term-rental.state';
import { OrderConfig } from '../../order/config/order.config';
import { AnyApproach, ApproachKey } from '../../order/interface/order.interface';
import { ComparableService } from '../../order/service/comparable.service';
import { ApproachFactoryState } from '../../order/state/approach-factory.state';
import { CostApproachState } from '../../order/state/cost-approach.state';
import { OrderState } from '../../order/state/order.state';
import { SalesApproachState } from '../../order/state/sales-approach.state';
import { PropertyService } from '../../property/service/property.service';
import { PropertyState } from '../../property/state/property.state';
import { ShortTermRentalState } from '../../short-term-rental/state/short-term-rental.state';
import { SyncScope } from '../../sync/interface/sync.interface';
import { SyncService } from '../../sync/service/sync.service';
import { ValidationState } from '../../validation/state/validation.state';
import { ScenarioApi } from '../api/scenario.api';
import { ScenarioConfig } from '../config/scenario.config';
import {
  CreateScenarioParam,
  Scenario,
  ScenarioColor,
  ScenarioMap,
  ScenarioTableData,
} from '../interface/scenario.interface';
import { ScenarioState } from '../state/scenario.state';

@Injectable({
  providedIn: 'root',
})
export class ScenarioService {
  private _activeScenarioId?: string | null;

  constructor(
    private _router: Router,
    private approachFactoryState: ApproachFactoryState,
    private asyncDataState: AsyncDataState,
    private comparableService: ComparableService,
    private costApproachState: CostApproachState,
    private longTermRentalState: LongTermRentalState,
    private orderState: OrderState,
    private progressLoaderService: ProgressLoaderService,
    private propertyService: PropertyService,
    private propertyState: PropertyState,
    private salesApproachState: SalesApproachState,
    private scenarioApi: ScenarioApi,
    private scenarioState: ScenarioState,
    private shortTermRentalState: ShortTermRentalState,
    private snackBarService: SnackBarService,
    private syncService: SyncService,
    private unitAdjustmentService: UnitAdjustmentService,
    private validationState: ValidationState,
    private storageService: StorageService
  ) {
    this._activeScenarioId = String(
      this.storageService.getItem(StorageType.SESSION_STORAGE, StorageScope.scenarioId, 'scenarioId') ?? ''
    );
  }

  public fetchScenarioList(orderId?: string): Observable<boolean> {
    const loaderId = this.progressLoaderService.show('Getting all Scenarios');
    return this.scenarioApi.getScenarioList(orderId ?? this.orderState.orderDetail?.id).pipe(
      map((res: Scenario[]) => {
        if (!res) return false;

        let activeScenario = res[0];
        const scenarioMap = this._convertScenarioListToMap(res);

        if (
          (this._activeScenarioId && !Object.keys(scenarioMap)?.includes(this._activeScenarioId)) ||
          !this._activeScenarioId
        ) {
          this._activeScenarioId = res.filter((sc) => sc.selected)?.[0]?.id;
        }

        activeScenario = scenarioMap[this._activeScenarioId];

        this.storageService.setItem(
          StorageType.SESSION_STORAGE,
          StorageScope.scenarioId,
          'scenarioId',
          this._activeScenarioId
        );

        this.syncService.syncLocally(SyncScope.SCENARIO_MAP, scenarioMap);
        this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

        return true;
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to load Scenario list.');
        return of(false);
      }),
      finalize(() => this.progressLoaderService.hide(loaderId))
    );
  }

  public fetchScenario(scenarioId: string): Observable<boolean> {
    const loaderId = this.progressLoaderService.show('Updating Scenario');
    return this.scenarioApi.getScenario(scenarioId).pipe(
      map((res) => {
        if (!res) return false;

        this._normalizeUnitAdjustmentTopology(res);
        this.syncService.syncLocally(SyncScope.SCENARIO, res);

        return true;
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to fetch Scenario.');
        return of(false);
      }),
      finalize(() => this.progressLoaderService.hide(loaderId))
    );
  }

  public getScenarioTableData(): Array<ScenarioTableData> {
    const scenarioData: Array<ScenarioTableData> = [];

    const approachTypeConfig = OrderConfig.approachTypeConfig;

    Object.values(this.scenarioState.scenarioMap ?? {})?.forEach((item) => {
      const scenario: ScenarioTableData = {
        id: item.id,
        name: item.name,
        values: [],
        createdAt: item.createdDate ? new Date(item.createdDate) : new Date(),
        selected: item.selected,
      };

      (Object.keys(approachTypeConfig) as Array<ApproachKey>).forEach((key) => {
        if (this.orderState.orderSetting?.[key]?.enabled) {
          scenario.values.push({
            ...approachTypeConfig[key],
            approachKey: key,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value: this.approachFactoryState.getScenarioDerivedValueOnType(
              item.id,
              approachTypeConfig[key].valuationType
            ),
          });
        }
      });

      scenarioData.push(scenario);
    });

    return scenarioData;
  }

  public createScenario(params: CreateScenarioParam): Observable<Scenario | undefined> {
    return this.scenarioApi.createScenario(params, this.orderState.orderDetail?.id).pipe(
      map((res: Scenario) => {
        if (!res) return;

        const scenarioMap = structuredClone(this.scenarioState.scenarioMap) ?? {};

        this._normalizeUnitAdjustmentTopology(res);
        scenarioMap[res.id] = res;

        this.syncService.syncLocally(SyncScope.SCENARIO_MAP, scenarioMap);

        return res;
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to create Scenario.');
        return of();
      })
    );
  }

  public deleteScenario(scenarioId: string): Observable<boolean> {
    return this.scenarioApi.deleteScenario(scenarioId).pipe(
      map(() => {
        const deletedScenario = this.scenarioState.scenarioMap?.[scenarioId];
        const scenarioMap = structuredClone(this.scenarioState.scenarioMap);
        delete scenarioMap?.[scenarioId];

        this.syncService.syncLocally(SyncScope.SCENARIO_MAP, scenarioMap);

        // Clean up comparables associated with the deleted scenario
        this.comparableService.clearComparablesForScenario(scenarioId);

        // Clean up subject property from Property Store
        if (deletedScenario?.property?.id) {
          this.orderState.propertyStore.delete(deletedScenario.property.id);
        }

        // If user deletes the active scenario, we should switch to the first available scenario.
        if (scenarioId == this.scenarioState.activeScenario?.id) {
          this.switchScenario(Object.values(this.scenarioState.scenarioMap ?? {})?.[0].id).subscribe();
        }

        return true;
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to delete Scenario.');
        return of(false);
      })
    );
  }

  public switchScenario(scenarioId: string): Observable<void> {
    return from(
      this._router.navigate(['orders', this.orderState.orderDetail.id, 'basic-info'], { queryParamsHandling: 'merge' })
    ).pipe(
      switchMap(() => {
        const newScenario = this.scenarioState.scenarioMap?.[scenarioId];

        const loaderId = this.progressLoaderService.show(`Loading '${newScenario?.name}' Scenario`);

        // this.scenarioState.activeScenario = newScenario ?? null;

        this.storageService.setItem(
          StorageType.SESSION_STORAGE,
          StorageScope.scenarioId,
          'scenarioId',
          newScenario?.id
        );

        this._clearScenarioState();
        this._initializeScenarioRelatedState();

        if (newScenario) this.syncService.syncLocally(SyncScope.SCENARIO, newScenario);

        this._clearScenarioState();

        this._initializeScenarioRelatedState();

        return this.propertyService
          .fetchSubjectPropertyDetail(this.orderState.reportType, newScenario?.property?.id)
          .pipe(
            finalize(() => this.progressLoaderService.hide(loaderId)),
            map(() => void 0)
          );
      })
    );
  }

  /**
   * update scenario
   * @param updateSet
   * @returns
   */
  public updateScenario(scenarioId: string, updateSet: Partial<Scenario>): Observable<boolean> {
    return this.scenarioApi.updateScenario(scenarioId, updateSet).pipe(
      map((res: boolean) => {
        if (!res) return false;

        //fetching scenarioId for unselecting while updating scenario selection for companies which support only single selection
        let scenarioIdForUnselect = '';
        if (
          this.orderState.orderSetting?.multipleScenario.scenarioSelectionLimit == 1 &&
          Object.keys(updateSet).includes('selected')
        ) {
          scenarioIdForUnselect = Object.values(this.scenarioState.scenarioMap ?? {})
            ?.filter((item) => item.selected)
            ?.map((item) => item.id)[0];
        }

        const scenarioMap = structuredClone(this.scenarioState.scenarioMap);

        if (scenarioMap) {
          scenarioMap[scenarioId] = { ...scenarioMap[scenarioId], ...updateSet };
          if (scenarioIdForUnselect)
            scenarioMap[scenarioIdForUnselect] = { ...scenarioMap[scenarioIdForUnselect], selected: false };
        }

        this.syncService.syncLocally(SyncScope.SCENARIO_MAP, scenarioMap);

        //syncing active scenario
        if (this.scenarioState.activeScenario?.id == scenarioId)
          this.scenarioState.activeScenario = { ...this.scenarioState.activeScenario, ...updateSet };
        else if (scenarioIdForUnselect && this.scenarioState.activeScenario?.id == scenarioIdForUnselect)
          this.scenarioState.activeScenario = { ...this.scenarioState.activeScenario, selected: false };

        return true;
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to update Scenario.');
        return of(false);
      })
    );
  }

  public getSelectedScenarioIds() {
    const ids: string[] = Object.keys(this.scenarioState.scenarioMap ?? {}).filter(
      (scenarioId) => this.scenarioState.scenarioMap?.[scenarioId]?.selected
    );
    return ids;
  }

  /**
   * Generates a color pallet to be used for a specific scenario across the app.
   */
  public getScenarioColor(scenarioId: string): ScenarioColor | undefined {
    const index = Object.values(this.scenarioState.scenarioMap ?? {}).findIndex(
      (scenario) => scenario.id == scenarioId
    );

    if (index < 0) return;

    return {
      primary: ScenarioConfig.scenarioColors[index],
      primaryDark: `${ScenarioConfig.scenarioColors[index]}26`,
      primaryLight: `${ScenarioConfig.scenarioColors[index]}26`,
      muted: `${ScenarioConfig.scenarioColors[index]}26`,
      mutedDark: `${ScenarioConfig.scenarioColors[index]}26`,
      mutedLight: `${ScenarioConfig.scenarioColors[index]}26`,
    };
  }

  /**
   * clear scenario related states while switching scenario
   */
  private _clearScenarioState() {
    this.syncService.syncLocally(SyncScope.SALES_SELECTED_COMPS, {});
    this.salesApproachState.comparableList = [];
    this.salesApproachState.comparableListLength = 0;
    this.salesApproachState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.COST_SELECTED_COMPS, {});
    this.costApproachState.comparableList = [];
    this.costApproachState.comparableListLength = 0;
    this.costApproachState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS, {});
    this.shortTermRentalState.comparableList = [];
    this.shortTermRentalState.comparableListLength = 0;
    this.shortTermRentalState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS, {});
    this.longTermRentalState.comparableList = [];
    this.longTermRentalState.comparableListLength = 0;
    this.longTermRentalState.secondarySearchFilter = null;

    this.asyncDataState.asyncPendingData = [];

    // this.validationState.validationSheetToggle = false;
    // this.validationState.validationAuditTime = null;
    this.validationState.selectedRule = null;

    // OrderConfig.routeTreeConfig.clearValidation();

    this.syncService.syncLocally(SyncScope.SUBJECT, null);
    this.propertyState.activePropertyData = null;
  }

  /**
   * Initial set up of scenario related states
   */
  private _initializeScenarioRelatedState() {
    if (this.orderState.orderSetting?.salesComparison)
      this.salesApproachState.secondarySearchFilter =
        this.orderState.orderSetting.salesComparison.secondarySearchFilter;

    this.costApproachState.secondarySearchFilter =
      this.orderState.orderSetting?.costApproach?.secondarySearchFilter ?? null;

    this.shortTermRentalState.secondarySearchFilter =
      this.orderState.orderSetting?.shortTermRental?.secondarySearchFilter ?? null;

    this.longTermRentalState.secondarySearchFilter =
      this.orderState.orderSetting?.longTermRental?.secondarySearchFilter ?? null;
  }

  /** Convert a scenario list to a map with scenarioId as the key */
  private _convertScenarioListToMap(scenarioList: Scenario[]): ScenarioMap {
    const scenarioMap: ScenarioMap = {};
    scenarioList.forEach((scenario) => {
      if (!scenario.id) return;

      const scenarioCopy = structuredClone(scenario);
      this._normalizeUnitAdjustmentTopology(scenarioCopy);
      scenarioMap[scenario.id] = scenarioCopy;
    });
    return scenarioMap;
  }

  /** Puts every approach's unit adjustments back on positional indices before the app reads them. */
  private _normalizeUnitAdjustmentTopology(scenario: Scenario): void {
    Object.values(ApproachKey).forEach((approachKey) => {
      const approach = scenario[approachKey] as AnyApproach | undefined;
      this.unitAdjustmentService.normalizeScopedTopology(approach?.adjustment?.unitAdjustment);
    });
  }
}
